import {
  type PartialProfile,
  getProfileResponseSchema,
  minimalProfileUpdateRequestSchema,
  minimalProfileUpdateResponseSchema,
  updateProfileResponseSchema,
} from "@mg/schemas/src/christo/profile";
import { z } from "zod";

import { API_URL } from "../config/env";
import { smartFetch } from "../utils/http";
import { login } from "../utils/slices/auth";
import { store } from "../utils/store";

const completeOnboardingResponseSchema = z.object({
  token: z.string(),
});

export async function completeOnboarding() {
  const json = await smartFetch(`${API_URL}/private/profile/onboard`, {
    method: "POST",
  });

  const parsedData = completeOnboardingResponseSchema.parse(json);

  return parsedData;
}

export async function getUserProfile() {
  const json = await smartFetch(`${API_URL}/private/profile`, {
    method: "GET",
  });

  const parsedData = getProfileResponseSchema.parse(json);

  return parsedData;
}

export async function updateCurrentUserProfile(payload: PartialProfile) {
  const json = await smartFetch(`${API_URL}/private/profile`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });

  // PUT returns the updated user document; GET adds profileStatus on top.
  const parsedData = updateProfileResponseSchema.parse(json);

  // Only update auth slice if we have a token
  const response = json as { token?: string };
  if (response.token) {
    store.dispatch(login({ token: response.token }));
  }

  return parsedData;
}

// Historical alias retained because a couple of MyExpertise / MyWork routes
// still import it. Prefer `updateCurrentUserProfile` in new code.
export const updateUserProfile = updateCurrentUserProfile;

export async function updateMinimalUserProfile(
  payload: {
    avatar?: string | null;
    name?: string;
    preferredName?: string;
    avatarBackgroundColor?: string;
    avatarTextColor?: string;
    uiLanguage?: string;
  },
  targetUserId?: string,
) {
  // Validate request payload
  minimalProfileUpdateRequestSchema.parse(payload);

  // When a MEANINGFUL_GIGS admin edits someone else's profile from
  // Manage Users, hit the `/minimal/:userId` form and DO NOT log
  // ourselves in as the freshly-minted token (it's the target user's
  // token, not ours). For self-edits we keep the original behavior of
  // refreshing our own session.
  const isOtherUser = !!targetUserId;
  const url = isOtherUser
    ? `${API_URL}/private/profile/minimal/${targetUserId}`
    : `${API_URL}/private/profile/minimal`;

  const json = await smartFetch(url, {
    method: "PUT",
    body: JSON.stringify(payload),
  });

  const parsedData = minimalProfileUpdateResponseSchema.parse(json);

  if (!isOtherUser) {
    store.dispatch(login({ token: parsedData.token }));
  }

  return parsedData;
}

export async function checkUsernameUniqueness(preferredName: string) {
  const json = await smartFetch(`${API_URL}/private/profile/check-username`, {
    method: "POST",
    body: JSON.stringify({ preferredName }),
  });

  return json as { isUnique: boolean };
}
